import { useState, useCallback, useEffect } from "react"

// Simple hash function for content
const hashContent = (content: any): string => {
  const str = JSON.stringify(content)
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }
  return hash.toString()
}

// PDF cache storage - shared across all components
const pdfCache: Map<string, string> = new Map()

interface UsePdfViewOptions {
  reportId: string
  sectionName: string
  editorContent: any
  mock?: boolean
}

interface UsePdfViewReturn {
  showPdfView: boolean
  pdfLoading: boolean
  pdfUrl: string | null
  togglePdfView: () => void
  loadPdfView: () => void
}

export const usePdfView = ({
  reportId,
  sectionName,
  editorContent,
  mock = false,
}: UsePdfViewOptions): UsePdfViewReturn => {
  const [showPdfView, setShowPdfView] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [currentContentHash, setCurrentContentHash] = useState<string>("")

  const loadPdfView = useCallback(async () => {
    if (!editorContent) {
      return
    }

    const contentHash = hashContent(editorContent)
    setCurrentContentHash(contentHash)

    // Check cache first
    if (pdfCache.has(contentHash)) {
      setPdfUrl(pdfCache.get(contentHash)!)
      setShowPdfView(true)
      return
    }

    // Generate new PDF
    setPdfLoading(true)
    try {
      const params = new URLSearchParams({
        section: sectionName,
      })
      if (mock) {
        params.append("mock", "true")
      }
      const response = await fetch(
        `/api/household_quarterly_reports/${reportId}/section_pdf?${params.toString()}`,
      )

      if (!response.ok) {
        throw new Error("Failed to generate PDF")
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)

      // Cache the blob URL
      pdfCache.set(contentHash, url)
      setPdfUrl(url)
      setShowPdfView(true)
    } catch (error) {
      console.error("Error loading PDF:", error)
      alert("Failed to load PDF preview")
    } finally {
      setPdfLoading(false)
    }
  }, [editorContent, reportId, sectionName, mock])

  const togglePdfView = useCallback(() => {
    if (showPdfView) {
      setShowPdfView(false)
    } else {
      loadPdfView()
    }
  }, [showPdfView, loadPdfView])

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl && !pdfCache.has(currentContentHash)) {
        URL.revokeObjectURL(pdfUrl)
      }
    }
  }, [pdfUrl, currentContentHash])

  return {
    showPdfView,
    pdfLoading,
    pdfUrl,
    togglePdfView,
    loadPdfView,
  }
}
